<template>
  <div ref="containerRef" class="tw:relative tw:inline-block" style="overflow: visible;">
    <div class="tw:cursor-pointer" @click.stop="toggle">
      <slot name="trigger">
        <ButtonPlayV2 variant="filled" text="clique" />
      </slot>
    </div>

    <transition name="dropdown-fade">
      <div
        v-if="internalOpen"
        ref="menuRef"
        class="tw:absolute tw:w-max tw:min-w-[160px] tw:bg-(--caption-bg-box) tw:rounded-lg tw:p-1 tw:shadow-lg tw:border tw:border-(--divider-color)"
        :class="positionClass[props.position]"
        style="z-index: 100010;"
      >
        <slot name="area">
          <button
            v-for="(option, index) in options"
            :key="index"
            type="button"
            class="tw:w-full tw:rounded-md tw:flex tw:items-center tw:gap-2 tw:px-3 tw:py-2 tw:text-left tw:border-none tw:bg-transparent tw:cursor-pointer hover:tw:bg-black/5 dark:hover:tw:bg-white/10 tw:transition-colors"
            @click.stop="selectOption(option)"
          >
            <IconGoogle v-if="option.icon" size="14px" :color="option.color ?? 'var(--fontcolor)'" :name="option.icon" />
            <TextPlay size="sm" :color="option.color ?? 'var(--fontcolor)'" :text="option.label" />
          </button>
        </slot>
      </div>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import IconGoogle from '../../atoms/IconGoogle/IconGoogle.vue'
import TextPlay from '../../atoms/TextPlay/TextPlay.vue'
import ButtonPlayV2 from '../ButtonPlayV2/ButtonPlayV2.vue'

export interface DropdownOption {
  label: string
  value?: string | number
  icon?: string
  color?: string
  [key: string]: unknown
}

const props = withDefaults(defineProps<{
  options?: DropdownOption[]
  position?: 'top' | 'bottom' | 'left' | 'right'
}>(), {
  options: () => [],
  position: 'bottom',
})

const emit = defineEmits<{
  select: [option: DropdownOption]
}>()

const containerRef = ref<HTMLElement | null>(null)
const menuRef = ref<HTMLElement | null>(null)
const internalOpen = ref(false)

const positionClass = {
  top: 'tw:bottom-full tw:right-0 tw:mb-1',
  bottom: 'tw:top-full tw:right-0 tw:mt-1',
  left: 'tw:top-0 tw:right-full tw:mr-1',
  right: 'tw:top-0 tw:left-full tw:ml-1',
} as const

function toggle() {
  internalOpen.value = !internalOpen.value
}

function selectOption(option: DropdownOption) {
  internalOpen.value = false
  emit('select', option)
}

function handleClickOutside(e: MouseEvent) {
  if (!internalOpen.value) return
  const target = e.target as Node
  if (containerRef.value?.contains(target)) return
  internalOpen.value = false
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
